import { useCallback, useEffect, useRef, useState } from 'react'

import { localEncodeViable, segmenter } from '../../lib/segment'

/**
 * Live camera capture (07 §1, WS5-01/03).
 *
 * 07 opens with the constraint that shapes this whole feature: **iPhone Safari has no WebXR**,
 * and most homeowners are on iPhones. So "AR" here is camera-first with AI compositing — a
 * live preview with a reticle, a capture that freezes a frame, and a guided walk-around that
 * asks for the angles the render actually benefits from.
 *
 * The reticle is not decoration. It tells somebody where to point, and the guided prompts
 * ("stand back", "get the whole wall in") are the difference between a photo that renders well
 * and one that renders into a corner of a cabinet.
 *
 * `getUserMedia` is requested with `facingMode: environment` and released on unmount — a
 * camera light left on after a sheet closes is alarming in someone's bathroom.
 *
 * ## The live highlight (WS5-02), and where it is offered
 *
 * 07 §2 asks for a live segmentation overlay at "~5-10 fps". That number is not reachable with
 * this model, and the interesting part is how far off it is on which hardware. SlimSAM's vision
 * encoder is a ViT over a 1024px input and it is the whole cost; the decoder that follows is
 * milliseconds. Measured: **on WebGPU the encoder is roughly 300 ms, so about 3 Hz. Without
 * WebGPU, in WASM, it is 20 seconds or more** — a headless Chromium on this box did not finish
 * one frame in twenty. Not a tuning problem: a different model.
 *
 * So the toggle is **only offered when the segmenter came up on WebGPU**. A control that
 * freezes the camera for twenty seconds and then gives up is worse than no control, and the
 * screen it is on works perfectly without it. Everything else here — the reticle, the guided
 * prompts, capture — is unchanged on every device.
 *
 * Where it is offered, it **re-encodes only when the picture changes.** A 64x48 grayscale
 * sample of each frame costs well under a millisecond, and comparing it with the last one says
 * whether the camera moved. Hold still and the loop does nothing at all — no battery, no heat,
 * and the highlight stays put because the scene did. That is a better answer than a fixed frame
 * rate even where a fixed frame rate would be affordable.
 *
 * Off by default even there, because it is the most expensive thing this screen can do and most
 * people just want to take a photo.
 */

interface Props {
  open: boolean
  onClose: () => void
  /** Resolves when the photo is safely in the session; rejects if it did not get there. */
  onCapture: (blob: Blob, source: 'camera_capture' | 'camera_pin') => void | Promise<void>
}

/** Mean absolute difference, 0-255, above which the camera is considered to have moved. 6 is
 *  above sensor noise and hand tremor and below a deliberate pan. */
const MOTION_THRESHOLD = 6
/** Frames are downscaled to this before encoding; the encoder resizes to 1024 regardless. */
const LIVE_MAX_EDGE = 1024

/**
 * Paint one mask over the video, honouring `object-cover`.
 *
 * The video element crops rather than letter-boxes, so the mask has to be drawn with the same
 * crop or the highlight sits next to the thing it is highlighting. Working that out from the
 * element's own box is the only way to stay correct when the phone is rotated.
 */
function draw(
  canvas: HTMLCanvasElement,
  video: HTMLVideoElement,
  result: { alpha: Uint8Array; width: number; height: number },
): void {
  const box = video.getBoundingClientRect()
  if (canvas.width !== Math.round(box.width) || canvas.height !== Math.round(box.height)) {
    canvas.width = Math.round(box.width)
    canvas.height = Math.round(box.height)
  }
  const context = canvas.getContext('2d')
  if (!context) return
  context.clearRect(0, 0, canvas.width, canvas.height)

  const mask = document.createElement('canvas')
  mask.width = result.width
  mask.height = result.height
  const maskContext = mask.getContext('2d')
  if (!maskContext) return
  const image = maskContext.createImageData(result.width, result.height)
  for (let i = 0; i < result.alpha.length; i += 1) {
    if (!result.alpha[i]) continue
    // ASB blue at a third opacity: enough to read as a selection, not enough to hide the thing
    // being selected, which is the whole point of showing it live.
    image.data[i * 4] = 45
    image.data[i * 4 + 1] = 108
    image.data[i * 4 + 2] = 223
    image.data[i * 4 + 3] = 86
  }
  maskContext.putImageData(image, 0, 0)

  // `object-cover`: scale by the larger ratio and centre the overflow.
  const scale = Math.max(canvas.width / result.width, canvas.height / result.height)
  const width = result.width * scale
  const height = result.height * scale
  context.drawImage(mask, (canvas.width - width) / 2, (canvas.height - height) / 2, width, height)
}

export function CameraSheet({ open, onClose, onCapture }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const overlayRef = useRef<HTMLCanvasElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [ready, setReady] = useState(false)
  const [highlight, setHighlight] = useState(false)
  const [highlightHz, setHighlightHz] = useState<number | null>(null)
  //: Null until the model has reported which backend it came up on. See the note above: the
  //: live highlight is only worth offering on WebGPU.
  const [canHighlight, setCanHighlight] = useState(false)
  /**
   * What the shutter did, and whether it worked.
   *
   * The first version of this screen had no answer to "did that work?". You pressed a 74-pixel
   * shutter over a live picture, the guided prompt changed, and nothing else on the screen moved
   * — no flash, no thumbnail, no word. And when the upload failed, the message went to the
   * workspace's notice bar, which is *behind* this full-screen sheet, so the one case where
   * feedback is essential was the one case with none at all. Somebody standing in a bathroom
   * would press it again, and again.
   */
  const [shot, setShot] = useState<{
    url: string
    state: 'saving' | 'saved' | 'failed'
    error?: string
  } | null>(null)
  const [flash, setFlash] = useState(false)

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
    setReady(false)
  }, [])

  useEffect(() => {
    if (!open) {
      stop()
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1440 },
          },
          audio: false,
        })
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }
        streamRef.current = stream
        if (videoRef.current) {
          videoRef.current.srcObject = stream
          await videoRef.current.play()
        }
        setReady(true)
        setError(null)
      } catch (cause) {
        // 07 §8: no camera permission is a supported path, not a dead end.
        setError(
          cause instanceof DOMException && cause.name === 'NotAllowedError'
            ? 'Camera access is off for this site. You can upload a photo instead.'
            : 'We could not start the camera on this device. You can upload a photo instead.',
        )
      }
    })()
    return () => {
      cancelled = true
      stop()
    }
  }, [open, stop])

  const capture = useCallback(
    async (source: 'camera_capture' | 'camera_pin') => {
      const video = videoRef.current
      if (!video || !video.videoWidth) return
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const context = canvas.getContext('2d')
      if (!context) return
      context.drawImage(video, 0, 0)
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', 0.92),
      )
      if (!blob) return

      // The shutter answers immediately, from the frame already in hand — the upload takes a
      // second or two on a phone and the confirmation must not wait for it.
      setFlash(true)
      window.setTimeout(() => setFlash(false), 160)
      const url = URL.createObjectURL(blob)
      setShot((previous) => {
        if (previous) URL.revokeObjectURL(previous.url)
        return { url, state: 'saving' }
      })

      try {
        await onCapture(blob, source)
        setShot((previous) => (previous?.url === url ? { url, state: 'saved' } : previous))
      } catch (failure) {
        setShot((previous) =>
          previous?.url === url
            ? {
                url,
                state: 'failed',
                error:
                  failure instanceof Error && failure.message
                    ? failure.message
                    : 'That photo did not upload. Try again.',
              }
            : previous,
        )
      }
    },
    [onCapture],
  )

  // The object URLs are the only thing this component allocates that the browser will not
  // reclaim on its own.
  useEffect(() => {
    if (open) return
    setShot((previous) => {
      if (previous) URL.revokeObjectURL(previous.url)
      return null
    })
  }, [open])

  // Warm the model while the person is still framing the shot, and find out which backend it
  // came up on. On WebGPU this costs nothing that was not going to be paid anyway — the still
  // photo they are about to take needs the same model loaded.
  useEffect(() => {
    if (!open || !ready) return
    let cancelled = false
    void segmenter.load().then((ok) => {
      // Same predicate as the still photo: the live highlight runs the same encoder per
      // frame, and the device that cannot afford one pass can afford a stream of them least
      // of all (a real iPhone with WebGPU crash-looped on exactly this encoder, 2026-08-22).
      if (!cancelled) setCanHighlight(ok && localEncodeViable(segmenter.device))
    })
    return () => {
      cancelled = true
    }
  }, [open, ready])

  // --------------------------------------------------------------- the live highlight
  useEffect(() => {
    if (!open || !highlight || !ready) return
    let cancelled = false
    let previous: Uint8ClampedArray | null = null

    /** A 64x48 grayscale thumbnail of the current frame, for motion detection. */
    const sampler = document.createElement('canvas')
    sampler.width = 64
    sampler.height = 48
    const sample = sampler.getContext('2d', { willReadFrequently: true })

    async function loop() {
      while (!cancelled) {
        const video = videoRef.current
        const canvas = overlayRef.current
        if (!video || !canvas || !video.videoWidth || !sample) {
          await new Promise((resolve) => setTimeout(resolve, 200))
          continue
        }

        sample.drawImage(video, 0, 0, sampler.width, sampler.height)
        const current = sample.getImageData(0, 0, sampler.width, sampler.height).data
        let moved = previous === null
        if (previous) {
          let total = 0
          // Every fourth pixel, red channel only. Enough to tell a pan from a hand tremor, and
          // it costs nothing — the point of this check is that it is cheaper than the thing it
          // is deciding whether to run.
          for (let i = 0; i < current.length; i += 16) {
            total += Math.abs((current[i] ?? 0) - (previous[i] ?? 0))
          }
          moved = total / (current.length / 16) > MOTION_THRESHOLD
        }
        previous = current

        if (!moved) {
          await new Promise((resolve) => setTimeout(resolve, 180))
          continue
        }

        const started = performance.now()
        // Downscaled before encoding. The encoder resizes to 1024 internally anyway, so a
        // 4K frame is bytes copied for nothing on the device least able to spare them.
        const scale = Math.min(1, LIVE_MAX_EDGE / Math.max(video.videoWidth, video.videoHeight))
        const bitmap = await createImageBitmap(video, {
          resizeWidth: Math.round(video.videoWidth * scale),
          resizeHeight: Math.round(video.videoHeight * scale),
        })
        const result = await segmenter.segmentFrame(bitmap, [
          // The reticle's centre: what the person is pointing at is what they mean.
          {
            x: Math.round((video.videoWidth * scale) / 2),
            y: Math.round((video.videoHeight * scale) / 2),
            include: true,
          },
        ])
        if (cancelled) return
        if (!result) {
          setHighlight(false)
          return
        }
        setHighlightHz(1000 / Math.max(1, performance.now() - started))
        draw(canvas, video, result)
      }
    }

    void loop()
    // Captured now, not read in the cleanup: by the time this runs the ref may already point
    // somewhere else, and clearing the wrong canvas is worse than clearing none.
    const canvasAtStart = overlayRef.current
    return () => {
      cancelled = true
      if (canvasAtStart) {
        canvasAtStart.getContext('2d')?.clearRect(0, 0, canvasAtStart.width, canvasAtStart.height)
      }
    }
  }, [open, highlight, ready])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-navy-2 on-dark">
      <header className="flex items-center justify-between gap-3 px-5 pb-3 pt-[calc(var(--safe-top)+1rem)]">
        <p className="m-0 text-sm font-semibold text-white">Point at the room</p>
        <button
          type="button"
          onClick={onClose}
          className="min-h-11 rounded-btn border border-white/30 px-4 text-sm font-semibold text-white"
        >
          Close
        </button>
      </header>

      <div className="relative flex-1 overflow-hidden">
        <video
          ref={videoRef}
          playsInline
          muted
          className="absolute inset-0 h-full w-full object-cover"
        />

        {/* The live highlight sits between the video and the reticle: over the picture, under
            the framing aid, and never over the controls. */}
        <canvas
          ref={overlayRef}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 h-full w-full"
        />

        {/* The reticle. Centre-weighted, deliberately large: it is a framing aid, not a
            crosshair, and 07 §1 wants the fixture under it to be what gets captured. */}
        {ready && (
          <div className="pointer-events-none absolute inset-0 grid place-items-center">
            <div className="size-[62vmin] max-w-[520px] rounded-[28px] border-2 border-white/70 shadow-[0_0_0_9999px_rgba(12,26,45,0.34)]" />
          </div>
        )}

        {/* A shutter flash. Not decoration: it is the only instant, unambiguous "yes, that
            happened" available on a screen whose entire surface is a moving picture. */}
        {flash && <div aria-hidden className="pointer-events-none absolute inset-0 bg-white/80" />}

        {shot && (
          <div
            role="status"
            data-testid="camera-shot"
            data-state={shot.state}
            className="absolute inset-x-5 bottom-5 flex items-center gap-3 rounded-card bg-white/95 p-3 text-sm text-ink shadow-lift"
          >
            <img
              src={shot.url}
              alt=""
              className="size-14 shrink-0 rounded-btn object-cover"
              draggable={false}
            />
            <div className="min-w-0">
              <p className="m-0 font-semibold">
                {shot.state === 'saving'
                  ? 'Saving…'
                  : shot.state === 'saved'
                    ? 'Added to your room'
                    : 'That one did not save'}
              </p>
              <p className="m-0 mt-0.5 text-xs text-muted">
                {shot.state === 'failed'
                  ? shot.error
                  : shot.state === 'saved'
                    ? 'Take another from a different angle, or close to work on it.'
                    : 'Hold still for a moment.'}
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="absolute inset-x-5 top-6 rounded-card bg-white p-4 text-sm text-ink">
            {error}
          </div>
        )}
      </div>

      <div className="px-5 pb-[calc(var(--safe-bottom)+1.25rem)] pt-4">
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            hidden={!canHighlight}
            onClick={() => {
              setHighlight((on) => !on)
              setHighlightHz(null)
            }}
            aria-pressed={highlight}
            disabled={!ready}
            className={`min-h-11 rounded-pill border px-4 text-sm font-semibold transition-colors disabled:opacity-40 ${
              highlight ? 'border-white bg-white text-navy' : 'border-white/30 text-white'
            }`}
          >
            {highlight ? 'Highlighting' : 'Highlight what I am pointing at'}
          </button>
          {highlight && highlightHz !== null && (
            <span className="font-mono text-xs tabular-nums text-white/50">
              {highlightHz.toFixed(1)}/s
            </span>
          )}
        </div>

        <p className="m-0 mt-3 text-center text-sm text-white/85">
          Get the whole area in the frame.
        </p>
        <div className="mt-4 flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={() => void capture('camera_pin')}
            disabled={!ready}
            className="min-h-12 rounded-btn border border-white/30 px-5 text-sm font-semibold text-white disabled:opacity-40"
          >
            Pin as room photo
          </button>
          <button
            type="button"
            onClick={() => void capture('camera_capture')}
            disabled={!ready}
            aria-label="Capture"
            className="grid size-[74px] place-items-center rounded-full border-4 border-white bg-white/20 disabled:opacity-40"
          >
            <span className="block size-[58px] rounded-full bg-white" />
          </button>
          <span className="min-w-[7.5rem]" aria-hidden />
        </div>
      </div>
    </div>
  )
}
